import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, uploadImport } from "@/api/client";
import { connectionSchemaQuery, useImports } from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field } from "@/components/ui/Field";
import { Select, Toggle } from "@/components/ui/Select";
import { UploadIcon } from "@/components/icons";
import { formatBytes } from "@/lib/bytes";

const MODES = [
  ["replace", "Replace — rebuild the table and re-infer column types"],
  ["append", "Append — add rows to the existing table, matched by column name"],
  ["create", "Create — fail if a table with this name already exists"],
] as const;

/**
 * CSV/TSV import into a source's managed DuckDB database (PRD FR-1.1).
 *
 * Type inference is DuckDB's; the panel shows what it decided, because a
 * zero-padded code silently becoming an integer is the failure this feature is
 * most likely to hide.
 */
export function ImportPanel({ connectionId }: { connectionId: string }) {
  const queryClient = useQueryClient();
  const { data: history } = useImports(connectionId, true);
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [tableName, setTableName] = useState("");
  const [mode, setMode] = useState<string>("replace");
  const [hasHeader, setHasHeader] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadImport(connectionId, file, { tableName: tableName.trim(), mode, hasHeader });
      await queryClient.invalidateQueries({ queryKey: ["connections", connectionId] });
      await queryClient.invalidateQueries({ queryKey: connectionSchemaQuery(connectionId).queryKey });
      setFile(null);
      setTableName("");
      if (fileInput.current) fileInput.current.value = "";
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "The import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-container border border-border bg-card p-5">
        <h3 className="type-h4 text-ink">Import a file</h3>
        <p className="type-support text-ink-secondary mt-1">
          CSV or TSV. Column types are inferred from the whole file, and codes like
          <code className="type-code text-ink bg-muted rounded-inner mx-1 px-1.5 py-0.5">007</code>
          stay text rather than becoming numbers.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label htmlFor="import-file" className="type-label text-ink">
              File
            </label>
            <input
              id="import-file"
              ref={fileInput}
              type="file"
              accept=".csv,.tsv,.tab,.txt,text/csv,text/tab-separated-values"
              onChange={(event) => {
                const chosen = event.target.files?.[0] ?? null;
                setFile(chosen);
                if (chosen && !tableName) setTableName(chosen.name.replace(/\.[^.]+$/, ""));
              }}
              className="type-support text-ink-secondary file:me-3 file:rounded-element file:border file:border-border file:bg-card file:px-3 file:py-2 file:type-label file:text-ink hover:file:bg-hover"
            />
            {file ? (
              <p className="type-support text-ink-secondary">
                {file.name} · {formatBytes(file.size)}
              </p>
            ) : null}
          </div>

          <Field
            label="Table name"
            value={tableName}
            placeholder="sales"
            hint="Normalised to lowercase with underscores."
            onChange={(event) => setTableName(event.target.value)}
          />

          <Select label="Mode" value={mode} onChange={(event) => setMode(event.target.value)}>
            {MODES.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>

          <div className="flex items-center">
            <Toggle
              label="First row is a header"
              checked={hasHeader}
              onChange={setHasHeader}
              hint="Off means columns are named column0, column1, …"
            />
          </div>
        </div>

        {error ? (
          <p role="alert" className="type-support text-error rounded-element bg-error-subtle mt-4 p-3">
            {error}
          </p>
        ) : null}

        <div className="mt-4">
          <Button
            variant="primary"
            icon={<UploadIcon className="size-4" />}
            loading={busy}
            disabled={!file}
            onClick={() => void submit()}
          >
            Import
          </Button>
        </div>
      </section>

      <section>
        <h3 className="type-h4 text-ink mb-3">Import history</h3>
        {history && history.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {history.map((record) => (
              <li key={record.id} className="rounded-container border border-border bg-card p-4">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="type-code text-ink">{record.table_name}</span>
                  <Chip tone="neutral">{record.mode}</Chip>
                  <span className="type-support text-ink-secondary tabular-nums">
                    {record.row_count.toLocaleString()} rows imported ·{" "}
                    {record.total_rows.toLocaleString()} in table
                  </span>
                </div>
                <p className="type-support text-ink-secondary mt-1">
                  {record.original_filename} · {formatBytes(record.source_bytes)} ·{" "}
                  {record.delimiter === "\t" ? "tab" : `“${record.delimiter}”`}-delimited ·{" "}
                  {new Date(record.imported_at).toLocaleString()}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {record.columns.map((column) => (
                    <span
                      key={column.name}
                      // type-support, not type-caption: the caption role
                      // uppercases, and these are real column identifiers.
                      className="type-support text-ink-secondary bg-muted rounded-full px-2 py-1"
                    >
                      {column.name} · {column.type}
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<UploadIcon className="size-8" />}
            title="Nothing imported yet"
            detail="Upload a CSV or TSV above and it becomes a queryable table in this source."
          />
        )}
      </section>
    </div>
  );
}
